// PMF build marker: header v3 — full-width tabs, ? in dropdown
import React, { useState, useEffect, lazy, Suspense } from "react";
import { ICLOUD_ALBUM, BOAT_NUMBER } from "../App";
import HERO_IMG from "../heroImage";
import { getRaceWeather, getRaceTemp } from "../weather";
import { computeRaceSog } from "../raceSog";
import { pmfPerf } from "../perf";
import Lightbox from "./Lightbox";
import PhotoCarousel from "./PhotoCarousel";
import PmfMasthead from "./PmfMasthead";
import { WindIcon, CompassIcon, ThermometerIcon, ConditionIcon, AiSparkle } from "./WeatherIcons";
import { uploadImages } from "../cloudinary";

// Heavy tab views split into their own chunks — loaded only when the tab opens.
const StatsTab = lazy(() => import("./StatsTab"));
const BoatTab = lazy(() => import("./BoatTab"));

// Tracks whether viewport is mobile-width (≤600px)

// ── Custom nautical nav icons (Set A: spinnaker / trophy / compass) ──
function NavIcon({ id, active, size = 24 }) {
  const stroke = active ? "var(--text)" : "var(--slate)";
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke, strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  if (id === "feed") {
    // spinnaker sail
    return (<svg {...common}><path d="M12 3 L19 18 L5 18 Z" /><line x1="12" y1="3" x2="12" y2="21" /></svg>);
  }
  if (id === "recap") {
    // trophy cup
    return (<svg {...common}><path d="M6 4h12v4a6 6 0 0 1-12 0Z" /><path d="M9 14h6M10 18h4M8 21h8" /></svg>);
  }
  if (id === "boat") {
    // helm / ship's wheel
    return (<svg {...common}><circle cx="12" cy="12" r="3" /><circle cx="12" cy="12" r="8" /><line x1="12" y1="1" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="23" /><line x1="1" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="23" y2="12" /></svg>);
  }
  // stats: compass with needle
  return (<svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 7 L14 12 L12 17 L10 12 Z" fill={stroke} stroke="none" /></svg>);
}
function useIsMobile() {
  const [mobile, setMobile] = useState(typeof window !== "undefined" ? window.innerWidth <= 600 : false);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth <= 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mobile;
}

export default function PublicReport({ races, signups, notRacing, boatFull, reactions, comments, schedule, onSignup, onRemoveSignup, onToggleReaction, onAddComment, onUpdateComment, onRemoveComment, onCrewPhotos, announcement, onEnterSkipper, boatInfo }) {
  const SCHEDULE = schedule || [];
  const isMobile = useIsMobile();
  // Render a stat with a unit, or an em-dash when the value is blank/missing.
  const stat = (val, unit = "") => {
    const v = (val === null || val === undefined) ? "" : String(val).trim();
    return v === "" ? "—" : `${v}${unit}`;
  };
  // Render "place/fleet", or an em-dash when place is missing (e.g. PMF sat out).
  const placeOf = (r) => (typeof r.place === "number" && r.place > 0) ? `${r.place}/${r.fleet || "—"}` : "—";

  // ── Crew counting ──
  // Each signup is one person. Skipper (Frederick) is always aboard, so every
  // upcoming race starts at 1.
  const crewCount = (date) => {
    const subs = signups.filter(s => s.race_date === date);
    return 1 + subs.length; // +1 = skipper, always aboard
  };
  // Colour by fullness: red at 12+, amber at 10-11, normal below.
  const crewLevel = (n) => n >= 12 ? "full" : (n >= 10 ? "warn" : "ok");
  const crewColors = { ok: { bg:"var(--surface2)", fg:"var(--text)", bd:"var(--border)" }, warn: { bg:"#fdf3e3", fg:"#9a6b00", bd:"#e3c890" }, full: { bg:"#fbeae8", fg:"#C0392B", bd:"#e0a89f" } };
  const deskMenuItem = { display:"flex", alignItems:"center", gap:10, width:"100%", padding:"12px 16px", background:"none", border:"none", borderBottom:"1px solid var(--surface2)", cursor:"pointer", textAlign:"left", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:14, fontWeight:500, color:"var(--text)" };
  const [activeTab, setActiveTab] = useState("feed"); // feed | recap | stats
  const [crewUploading, setCrewUploading] = useState(null); // race id being uploaded to
  const notRacingSet = new Set(notRacing || []);
  const boatFullSet = new Set(boatFull || []);
  const REACTIONS = [
    { emoji:"👏", label:"Kudos" },
    { emoji:"🚀", label:"Send it" },
    { emoji:"😬", label:"Yikes" },
    { emoji:"🏆", label:"Podium" },
  ];
  const hStyle = { fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:20, fontWeight:600, color:"var(--text)", marginTop:22, marginBottom:6 };
  const fbInput = { width:"100%", background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:8, padding:"11px 13px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:15, color:"var(--ink)", outline:"none", marginBottom:10, boxSizing:"border-box" };
  const btnGradient = "linear-gradient(135deg, var(--navy) 0%, #16304d 60%, #1d3e63 100%)";

  // Share a race recap via the native share sheet (falls back to copying the link)
  // The public site is live at pardonmyfrenchracing.com — share the pretty
  // domain regardless of which host the app is being viewed on.
  const SHARE_ORIGIN = "https://pardonmyfrenchracing.com";

  const shareRace = async (r) => {
    // /race/N is served by a Netlify function that returns a per-race link
    // preview (cover photo + title), then redirects real visitors to #race-N.
    const url = `${SHARE_ORIGIN}/race/${r.raceNo}`;
    const place = (typeof r.place === "number") ? `${r.place}/${r.fleet}` : "";
    const text = `Pardon My French — Race ${r.raceNo} recap${place ? ` (finished ${place})` : ""}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `PMF Race ${r.raceNo} Recap`, text, url });
      } else {
        await navigator.clipboard.writeText(url);
        alert("Recap link copied to clipboard");
      }
    } catch (e) { /* user cancelled */ }
  };

  // Share an upcoming race so friends can sign up. Deep-links to the next-race
  // card (#upcoming-N); falls back to copying the link.
  const shareNextRace = async (s) => {
    const url = `${SHARE_ORIGIN}/#upcoming-${s.raceNo}`;
    const text = `Join the crew for Pardon My French — Race ${s.raceNo}, ${fmtDateLong(s.date)}. Add your name:`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `PMF Race ${s.raceNo} — sign up`, text, url });
      } else {
        await navigator.clipboard.writeText(url);
        alert("Race link copied to clipboard");
      }
    } catch (e) { /* user cancelled */ }
  };
  const pStyle = { marginBottom:14 };
  const ulStyle = { margin:"0 0 14px", paddingLeft:0, listStyle:"none" };
  const liStyle = { marginBottom:12, paddingLeft:16, borderLeft:"2px solid var(--gold)" };
  const [signupName, setSignupName] = useState({});
  const [confirmState, setConfirmState] = useState(null); // { date, name }
  const [confirmed, setConfirmed] = useState({}); // { date: [name, name] }
  // Track signups this person created (persisted in their browser) so only they can remove their own.
  const MY_SIGNUPS_KEY = "pmf_my_signups";
  const [mySignups, setMySignups] = useState(() => {
    try { return JSON.parse(localStorage.getItem(MY_SIGNUPS_KEY) || "[]"); }
    catch { return []; }
  });
  const rememberMySignup = (date, name) => {
    setMySignups(prev => {
      const next = [...prev, { date, name: name.toLowerCase() }];
      try { localStorage.setItem(MY_SIGNUPS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const forgetMySignup = (date, name) => {
    setMySignups(prev => {
      const next = prev.filter(m => !(m.date === date && m.name === name.toLowerCase()));
      try { localStorage.setItem(MY_SIGNUPS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const isMine = (date, name) => mySignups.some(m => m.date === date && m.name === (name || "").toLowerCase());

  // Jump to a race recap: switch to the Feed tab (where recaps live), then scroll
  // to the race card once it's rendered. Works from any tab.
  const goToRace = (raceNo) => {
    setActiveTab("feed");
    setTimeout(() => {
      const el = document.getElementById(`race-${raceNo}`);
      if (el) {
        const scroller = document.querySelector("[data-pmf-scroll]");
        if (scroller && scroller.contains(el)) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }, 80);
  };

  // Menu navigation: switch tab, close menus, then scroll to an optional anchor.
  const navTo = (tab, anchorId) => {
    setShowDeskMenu(false);
    setActiveTab(tab);
    try { window.history.replaceState(null, "", "#" + (TAB_HASH[tab] || tab)); } catch (e) {}
    if (anchorId) {
      setTimeout(() => {
        const el = document.getElementById(anchorId);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 90);
    } else {
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 60);
    }
  };

  // ── Shareable tab links ──────────────────────────────────────
  // Each tab maps to a friendly URL hash, so a copied or shared link reopens
  // straight to that tab.
  const TAB_HASH = { feed: "feed", recap: "season", stats: "stats", boat: "boat" };
  const HASH_TAB = { feed: "feed", season: "recap", stats: "stats", boat: "boat" };
  const goTab = (id) => {
    setActiveTab(id);
    try { window.history.replaceState(null, "", "#" + (TAB_HASH[id] || id)); } catch (e) {}
  };

  // Track comment IDs this person created (in their browser) so only they can edit/remove their own.
  const MY_COMMENTS_KEY = "pmf_my_comments";
  const [myComments, setMyComments] = useState(() => {
    try { return JSON.parse(localStorage.getItem(MY_COMMENTS_KEY) || "[]"); }
    catch { return []; }
  });
  const rememberMyComment = (id) => {
    if (!id) return;
    setMyComments(prev => {
      const next = [...prev, id];
      try { localStorage.setItem(MY_COMMENTS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const forgetMyComment = (id) => {
    setMyComments(prev => {
      const next = prev.filter(x => x !== id);
      try { localStorage.setItem(MY_COMMENTS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const isMyComment = (id) => myComments.includes(id);
  const [editingComment, setEditingComment] = useState(null); // { id, text }
  const [removeCommentConfirm, setRemoveCommentConfirm] = useState(null); // { id }

  const [removeConfirm, setRemoveConfirm] = useState(null); // { id, date, name }
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [weather, setWeather] = useState({});       // { date: {windSpeed,...} }
  const [raceTemps, setRaceTemps] = useState({});    // { date: 64 } for completed races
  const [perfHelp, setPerfHelp] = useState(false);   // PMF Perf "?" explainer in recap header
  const [reactName, setReactName] = useState({});    // { date: pendingEmoji } when asking for name
  const [reactPrompt, setReactPrompt] = useState(null); // { date, emoji }
  const [reactNameInput, setReactNameInput] = useState("");
  const [commentModal, setCommentModal] = useState(null); // { date } when open
  const [cmName, setCmName] = useState("");
  const [cmText, setCmText] = useState("");
  const [aiGenCount, setAiGenCount] = useState(0); // AI uses this comment session (max 2)
  const [aiGenLoading, setAiGenLoading] = useState(false);
  const [aiLimitMsg, setAiLimitMsg] = useState(false);
  const [savedName, setSavedName] = useState(""); // remembered across the session
  const [weatherRefreshing, setWeatherRefreshing] = useState({}); // { date: bool }
  const [showFeedback, setShowFeedback] = useState(false);
  const [fbName, setFbName] = useState("");
  const [fbEmail, setFbEmail] = useState("");
  const [fbText, setFbText] = useState("");
  const [lightbox, setLightbox] = useState(null);    // { photos: [], index: 0 }
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showDeskMenu, setShowDeskMenu] = useState(false);
  const [showWelcome, setShowWelcome] = useState(() => {
    try { return !localStorage.getItem("pmf_welcome_seen"); } catch { return false; }
  });
  const dismissWelcome = () => {
    try { localStorage.setItem("pmf_welcome_seen", "1"); } catch {}
    setShowWelcome(false);
  };


  const raceMap = new Map(races.map(r => [r.date, r]));
  const completed = SCHEDULE.filter(s => raceMap.has(s.date));
  // Upcoming = scheduled, not yet recorded, dated strictly AFTER today (local).
  // Today's race is the current race, not "upcoming", so it's excluded here.
  const _t = new Date();
  const todayStr = `${_t.getFullYear()}-${String(_t.getMonth()+1).padStart(2,"0")}-${String(_t.getDate()).padStart(2,"0")}`;
  const upcoming  = SCHEDULE.filter(s => !raceMap.has(s.date) && s.date > todayStr);
  // Featured next-race card includes TODAY's race (>= today) so it shows on race day.
  const featured  = SCHEDULE.filter(s => !raceMap.has(s.date) && s.date >= todayStr);

  // Fetch weather for a single date (used on mount and by the refresh button)
  const fetchWeatherFor = (date) => {
    setWeatherRefreshing(prev => ({ ...prev, [date]: true }));
    return getRaceWeather(date)
      .then(w => setWeather(prev => ({ ...prev, [date]: w })))
      .catch(() => {})
      .finally(() => setWeatherRefreshing(prev => ({ ...prev, [date]: false })));
  };

  // Fetch weather for the races the card actually shows as "upcoming" (skipping
  // any marked "not racing"), so the displayed next race always has its forecast.
  // Includes a one-week past tolerance so a race awaiting results still shows
  // conditions instead of a blank panel. Open-Meteo forecasts run ~16 days out.
  useEffect(() => {
    const nrSet = new Set(notRacing || []);
    featured.filter(s => !nrSet.has(s.date)).slice(0, 3).forEach(s => {
      const daysOut = (new Date(s.date + "T12:00:00") - new Date()) / 86400000;
      if (daysOut >= -7 && daysOut <= 16) fetchWeatherFor(s.date);
    });
  }, [races, notRacing]); // eslint-disable-line

  // Fetch race-day temperature for completed races (historical)
  useEffect(() => {
    races.forEach(r => {
      if (r.date && raceTemps[r.date] === undefined) {
        getRaceTemp(r.date)
          .then(t => setRaceTemps(prev => ({ ...prev, [r.date]: t })))
          .catch(() => setRaceTemps(prev => ({ ...prev, [r.date]: null })));
      }
    });
  }, [races]); // eslint-disable-line

  // (Deep-link scroll is handled in App.jsx once loading completes.)
  // If the page opens with a #race-N or #upcoming-N hash, make sure the Feed tab
  // (where both recaps and the next-race card live) is active so the scroll
  // target exists. The actual scroll is handled in App.jsx once loading settles.
  useEffect(() => {
    const h = window.location.hash;
    const pathRace = window.location.pathname.match(/\/race\/(\d+)/);
    if (pathRace || h.startsWith("#race-") || h.startsWith("#upcoming-")) { setActiveTab("feed"); return; }
    const tab = HASH_TAB[h.slice(1)];
    if (tab) setActiveTab(tab);
  }, []); // eslint-disable-line


  const placed     = races.filter(r => typeof r.place === "number");
  const bestPlace  = placed.length ? Math.min(...placed.map(r => r.place)) : null;
  const bestMaxSog = placed.length ? Math.max(...placed.map(r => parseFloat(r.maxSog)||0)) : null;
  const bestRaceSog = placed.length ? Math.max(...placed.map(r => parseFloat(computeRaceSog(r.distance, r.time))||0)) : null;
  const bestMaxTws = placed.length ? Math.max(...placed.map(r => parseFloat(r.maxTws)||0)) : null;
  // Season record summary
  const racesSailed = placed.length;

  // Crew podium — who actually raced most, from each race's recorded crew.
  // Frederick is the skipper (always aboard), so he doesn't count here.
  const crewCounts = {};
  placed.forEach(r => (Array.isArray(r.crew) ? r.crew : []).forEach(m => {
    const nm = String(m || "").trim();
    if (nm && nm.toLowerCase() !== "frederick") crewCounts[nm] = (crewCounts[nm] || 0) + 1;
  }));
  const crewBoard = Object.entries(crewCounts).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const avgPlace = placed.length ? Math.round(placed.reduce((a, r) => a + r.place, 0) / placed.length * 10) / 10 : null;
  const bestPlaceRace = bestPlace !== null ? placed.find(r => r.place === bestPlace) : null;

  const sorted = [...races].sort((a,b) => b.date.localeCompare(a.date));
  const signupsFor = (date) => (signups || []).filter(s => s.race_date === date);

  const requestSignup = (date) => {
    const name = (signupName[date] || "").trim();
    if (!name) return;
    setConfirmState({ date, name });
  };

  const confirmSignup = async () => {
    if (!confirmState || confirmLoading) return;
    setConfirmLoading(true);
    const { date, name } = confirmState;
    await onSignup(date, name);
    rememberMySignup(date, name);
    setConfirmed(c => ({ ...c, [date]: [...(c[date] || []), name] }));
    setSignupName(n => ({ ...n, [date]: "" }));
    setConfirmState(null);
    setConfirmLoading(false);
  };

  const generateAiComment = async () => {
    if (aiGenLoading) return;
    if (aiGenCount >= 2) { setAiLimitMsg(true); return; }
    const seed = cmText.trim();
    if (!seed) return;
    setAiGenLoading(true);
    try {
      const race = raceMap.get(commentModal.date);
      const ctx = race
        ? `Race context: Race ${race.raceNo}, finished ${race.place}/${race.fleet} in class ${race.raceClass || "?"}, top speed ${race.maxSog || "?"} kt, wind ${race.maxTws || "?"} kt.`
        : "";
      const res = await fetch("/api/narrate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          max_tokens: 200,
          system: "You polish a sailing crew member's rough notes into a short, natural comment for a race recap page. Rules: 1-2 sentences max. Warm, upbeat, first person, casual crew voice. Keep it genuine, not corporate or over-hyped. Never use em dashes. Output ONLY the comment text, no quotes, no preamble.",
          messages: [{ role: "user", content: `${ctx}\n\nMy rough notes: "${seed}"\n\nPolish this into a short race comment.` }],
        }),
      });
      const data = await res.json();
      const text = (data?.content?.find(b => b.type === "text")?.text || "").trim();
      if (text) {
        setCmText(text);
        setAiGenCount(c => c + 1);
      }
    } catch (e) {
      // fail quietly, keep their text
    }
    setAiGenLoading(false);
  };

  const sectionCard = { background:"var(--surface)", border:"1px solid var(--border)", borderRadius:10, overflow:"hidden", boxShadow:"0 2px 14px rgba(0,0,0,0.07)" };

  const completedSection = completed.length > 0 ? (
        <div id="completed-races" style={{ ...sectionCard, marginBottom: isMobile ? 24 : 32 }}>
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:12, padding: isMobile ? "18px 16px 4px" : "22px 26px 4px" }}>
            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--gold-deep, #8B6508)", fontWeight:700, paddingTop:6 }}>🏁 Completed races</div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              <a href="https://www.downtownsailingseries.com/race-info" target="_blank" rel="noreferrer" style={{ display:"flex", alignItems:"center", gap:5, background:"transparent", color:"var(--slate)", border:"1px solid var(--border)", borderRadius:999, padding:"6px 14px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.1em", textTransform:"uppercase", textDecoration:"none", fontWeight:600 }}>Race Info ↗</a>
              <a href={ICLOUD_ALBUM} target="_blank" rel="noreferrer" style={{ display:"flex", alignItems:"center", gap:5, background:"transparent", color:"var(--slate)", border:"1px solid var(--border)", borderRadius:999, padding:"6px 14px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.1em", textTransform:"uppercase", textDecoration:"none", fontWeight:600 }}>Race Photos ↗</a>
            </div>
          </div>
          <div style={{ padding: isMobile ? "0 16px 14px" : "0 26px 18px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, color:"var(--slate)", letterSpacing:"0.15em", textTransform:"uppercase" }}>
            {completed.length} race{completed.length!==1?"s":""} sailed
          </div>
          {isMobile ? (
            <div style={{ padding:"4px 14px 16px", display:"grid", gap:10 }}>
              {completed.map(s2 => {
                const r = raceMap.get(s2.date);
                const isBP = bestPlace !== null && r.place === bestPlace;
                const raceSog = computeRaceSog(r.distance, r.time);
                return (
                  <div key={r.id} onClick={()=>goToRace(r.raceNo)} style={{ display:"block", cursor:"pointer", textDecoration:"none", background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:8, padding:"12px 14px" }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                      <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--slate)" }}>Race {r.raceNo} · {fmtDate(r.date)}</span>
                      <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:14, fontWeight:700, color: isBP ? "var(--green)" : "var(--text)" }}>{r.didNotRace ? "sat out" : placeOf(r)}</span>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"4px 14px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, color:"var(--ink-soft)" }}>
                      <span>⏱ {stat(r.time)}</span>
                      <span>Race SOG {raceSog ? `${raceSog} kt` : "—"}</span>
                      <span>Max TWS {stat(r.maxTws, " kt")}</span>
                    </div>
                    <div style={{ marginTop:8, fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, color:"var(--gold)", fontWeight:700 }}>Read report →</div>
                  </div>
                );
              })}
            </div>
          ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:15 }}>
              <thead>
                <tr style={{ background:"var(--navy)" }}>
                  {["#","Date","Class","Place","Time","Max SOG","Race SOG","Avg TWS","Max TWS","Report"]
                    .map((h,i) => (
                    <th key={h} style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase", color: i===0 ? "var(--gold-lt)" : "rgba(255,255,255,0.7)", padding:"11px 14px", textAlign:"left", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {completed.map(s2 => {
                  const r = raceMap.get(s2.date);
                  const isBP = bestPlace  !== null && r.place === bestPlace;
                  const iBM = bestMaxSog !== null && parseFloat(r.maxSog) === bestMaxSog;
                  const raceSog = computeRaceSog(r.distance, r.time);
                  const iBRace = bestRaceSog !== null && bestRaceSog > 0 && parseFloat(raceSog) === bestRaceSog;
                  const iBMaxTws = bestMaxTws !== null && parseFloat(r.maxTws) === bestMaxTws;
                  return (
                    <tr key={r.id} style={{ borderBottom:"1px solid var(--border)" }}>
                      <td style={td}>{r.raceNo}</td>
                      <td style={td}>{fmtDate(r.date)}</td>
                      <td style={td}>{r.raceClass}</td>
                      <td style={{ ...td, color: isBP ? "#1a6b2e":"var(--ink-soft)", fontWeight: isBP?700:400 }}>{r.didNotRace ? "sat out" : placeOf(r)}</td>
                      <td style={td}>{stat(r.time)}</td>
                      <td style={{ ...td, color: iBM ? "#1a6b2e":"var(--ink-soft)", fontWeight: iBM?700:400 }}>{stat(r.maxSog, " kt")}</td>
                      <td style={{ ...td, color: iBRace ? "#1a6b2e":"var(--ink-soft)", fontWeight: iBRace?700:400 }}>{raceSog ? `${raceSog} kt` : "—"}</td>
                      <td style={td}>{stat(r.avgTws, " kt")}</td>
                      <td style={{ ...td, color: iBMaxTws ? "#1a6b2e":"var(--ink-soft)", fontWeight: iBMaxTws?700:400 }}>{stat(r.maxTws, " kt")}</td>
                      <td style={td}><span onClick={()=>goToRace(r.raceNo)} style={{ color:"var(--gold)", fontWeight:700, textDecoration:"none", cursor:"pointer" }}>Read →</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>
  ) : null;

  const upcomingSection = upcoming.length > 0 ? (
        <div id="season-table" style={{ ...sectionCard, margin: isMobile ? "0 0 26px" : "0 0 38px" }}>
          <div style={{ padding: isMobile ? "18px 16px 4px" : "22px 26px 4px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--gold-deep, #8B6508)", fontWeight:700 }}>📅 Calendar</div>
          <div style={{ padding: isMobile ? "0 16px 14px" : "0 26px 18px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, color:"var(--slate)", letterSpacing:"0.15em", textTransform:"uppercase" }}>
            Thursdays · dock departs 6:15 pm · sign up below
          </div>
          {isMobile ? (
            <div style={{ padding:"4px 14px 16px", display:"grid", gap:8 }}>
              {upcoming.map(s2 => {
                const nr = notRacingSet.has(s2.date);
                const sus = signupsFor(s2.date);
                return (
                  <div key={s2.date} style={{ padding:"10px 12px", background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:6 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, color:"var(--slate)" }}>Race {s2.raceNo} · {fmtDate(s2.date)}</span>
                      {nr && <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, fontWeight:700, color:"var(--clay, #B0703C)" }}>Not racing</span>}
                    </div>
                    {!nr && (
                      <>
                        {sus.length>0 && <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, color:"var(--slate)", marginTop:6 }}>{sus.map(s=>s.name).join(", ")}</div>}
                        {(boatFullSet.has(s2.date) || crewCount(s2.date) >= 12) ? (
                          <div style={{ marginTop:8, display:"inline-flex", alignItems:"center", gap:7, fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, fontWeight:700, letterSpacing:"0.06em", color:"var(--clay, #B0703C)" }}>Boat full · signups closed</div>
                        ) : (
                          <div style={{ display:"flex", gap:6, marginTop:8 }}>
                            <input value={signupName[s2.date]||""} onChange={e=>setSignupName(n=>({...n,[s2.date]:e.target.value}))} onKeyDown={e=>{if(e.key==="Enter")requestSignup(s2.date);}} placeholder="Add your name" style={{ flex:1, background:"var(--surface)", color:"var(--ink)", border:"1px solid var(--border)", borderRadius:6, padding:"7px 10px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, minWidth:0 }} />
                            <button onClick={()=>requestSignup(s2.date)} style={{ background: btnGradient, color:"var(--gold-lt)", border:"none", borderRadius:6, padding:"7px 14px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", cursor:"pointer", whiteSpace:"nowrap" }}>I'm in</button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:15 }}>
              <thead>
                <tr style={{ background:"var(--navy)" }}>
                  {["#","Date","Sign up"].map((h,i) => (
                    <th key={h} style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase", color: i===0 ? "var(--gold-lt)" : "rgba(255,255,255,0.7)", padding:"11px 14px", textAlign:"left", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {upcoming.map(s2 => {
                  const nr = notRacingSet.has(s2.date);
                  const sus = signupsFor(s2.date);
                  return (
                    <tr key={s2.date} style={{ borderBottom:"1px solid var(--border)", background:"var(--surface2)" }}>
                      <td style={{ ...td, color:"var(--slate)", fontWeight:400, verticalAlign:"top" }}>{s2.raceNo}</td>
                      <td style={{ ...td, color:"var(--slate)", verticalAlign:"top" }}>{fmtDate(s2.date)}</td>
                      {nr ? (
                        <td style={{ ...td, color:"var(--clay, #B0703C)", fontSize:13, fontWeight:700 }}>PMF not racing</td>
                      ) : (
                        <td style={td}>
                          {(() => {
                            const isFull = boatFullSet.has(s2.date) || crewCount(s2.date) >= 12;
                            const n = crewCount(s2.date);
                            const lvl = crewLevel(n);
                            const col = crewColors[lvl];
                            return (
                              <div>
                                <div style={{ display:"flex", alignItems:"baseline", gap:10, flexWrap:"wrap" }}>
                                  <span style={{ fontSize:14, fontWeight:800, color:"var(--text)", whiteSpace:"nowrap" }}>{n} <span style={{ fontWeight:600, color:"var(--slate)" }}>aboard</span></span>
                                  {sus.length>0 && (
                                    <span style={{ display:"inline-flex", flexWrap:"wrap", gap:"2px 12px", fontSize:14, color:"var(--ink-soft)" }}>
                                      {sus.map((su) => {
                                        const mine = isMine(s2.date, su.name);
                                        return (
                                          <span key={su.id} style={{ whiteSpace:"nowrap", display:"inline-flex", alignItems:"baseline" }}>
                                            {su.name}
                                            {mine && (
                                              <button onClick={()=>setRemoveConfirm({ id: su.id, date: s2.date, name: su.name })} title="Remove yourself" style={{ background:"none", border:"none", cursor:"pointer", color:"var(--slate)", fontSize:14, lineHeight:1, padding:"0 0 0 4px", fontWeight:700 }}>×</button>
                                            )}
                                          </span>
                                        );
                                      })}
                                    </span>
                                  )}
                                </div>
                                {isFull ? (
                                  <div style={{ marginTop:12, display:"inline-flex", alignItems:"center", gap:7, fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, fontWeight:700, letterSpacing:"0.06em", color:"var(--clay, #B0703C)" }}>Boat full · signups closed</div>
                                ) : (
                                  <div style={{ display:"flex", gap:8, marginTop:12 }}>
                                    <input value={signupName[s2.date]||""} onChange={e=>setSignupName(nn=>({...nn,[s2.date]:e.target.value}))} onKeyDown={e=>{if(e.key==="Enter")requestSignup(s2.date);}} placeholder="Add your name" style={{ flex:1, minWidth:0, background:"var(--surface)", color:"var(--ink)", border:"1px solid var(--border)", borderRadius:6, padding:"8px 12px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:14 }} />
                                    <button onClick={()=>requestSignup(s2.date)} style={{ flexShrink:0, background: btnGradient, color:"var(--gold-lt)", border:"none", borderRadius:6, padding:"8px 16px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", cursor:"pointer", whiteSpace:"nowrap" }}>I'm in</button>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>
  ) : null;

  // Next race placement: within 7 days it sits on top of the feed; further out
  // it drops beneath the latest race recap.
  const daysToNext = (() => { const _nr = featured.filter(s => !notRacingSet.has(s.date))[0]; return _nr ? Math.ceil((new Date(_nr.date + "T12:00:00").getTime() - Date.now()) / 86400000) : null; })();
  const nextOnTop = daysToNext == null ? true : daysToNext <= 7;
  const nextRaceBlock = featured.length > 0 ? (
          <div style={{ margin: isMobile ? "24px 0 0" : "36px 0 0" }}>
            {showAllUpcoming && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--gold)", fontWeight:600 }}>
                  Upcoming races — sign up
                </div>
              </div>
            )}
            <div style={{ display:"grid", gridTemplateColumns: showAllUpcoming ? "repeat(auto-fill,minmax(280px,1fr))" : "1fr", gap:14, alignItems:"stretch" }}>
              {(showAllUpcoming ? featured : (featured.filter(s => !notRacingSet.has(s.date)).slice(0, 1))).map(s => {
                const crew = signupsFor(s.date);
                const recentlyAdded = confirmed[s.date] || [];
                const isNotRacing = notRacingSet.has(s.date);
                const isFull = boatFullSet.has(s.date) || crewCount(s.date) >= 12;
                const w = weather[s.date];
                const isNext = !showAllUpcoming;
                return (
                  <div key={s.date} id={`upcoming-${s.raceNo}`} style={{ display:"flex", flexDirection:"column", background:"var(--surface)", border: isNotRacing ? "2px solid #c0392b" : "1px solid var(--border)", borderRadius:10, padding:"22px 24px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", textAlign: (isNext && isMobile) ? "center" : "left", boxSizing:"border-box", overflow:"hidden" }}>
                    {isNext && !isNotRacing ? (
                      <div style={{ background:"linear-gradient(135deg, #3A2A05 0%, #5C4308 55%, #7A5A0C 100%)", margin:"-22px -24px 18px", padding: isMobile ? "16px 18px" : "20px 28px", textAlign:"left", display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12 }}>
                        <div>
                          <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.28em", color:"#F0DFA8", textTransform:"uppercase", fontWeight:700 }}>Race {s.raceNo} · Up next</div>
                          <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:700, fontSize: isMobile ? 24 : 28, lineHeight:1.1, margin:"6px 0 0", color:"#FFF7E6" }}>{fmtDateLong(s.date)}</div>
                        </div>
                        <button
                          onClick={() => shareNextRace(s)}
                          aria-label="Share this race"
                          title="Share this race"
                          style={{ flexShrink:0, width:38, height:38, borderRadius:999, background:"rgba(255,255,255,0.12)", border:"1px solid rgba(240,223,168,0.5)", color:"#FFF7E6", display:"inline-flex", alignItems:"center", justifyContent:"center", cursor:"pointer", padding:0 }}
                        >
                          <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 3v12" /><path d="M8 7l4-4 4 4" /><path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                    <div style={{ marginBottom: isNext ? 16 : 12 }}>
                      <div>
                        <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--slate)", marginBottom:4 }}>
                          Race {s.raceNo}
                        </div>
                        <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isNext ? 26 : 22, fontWeight:600, color:"var(--text)" }}>
                          {isNext
                            ? <>Next race {fmtDateLong(s.date)}</>
                            : fmtDateLong(s.date)}
                        </div>
                      </div>
                    </div>
                    )}

                    {isNotRacing ? (
                      <div style={{ flex:1, display:"flex", alignItems:"center" }}>
                        <div style={{ width:"100%", background:"#c0392b", color:"#fff", borderRadius:8, padding:"12px 14px", textAlign:"center", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, fontWeight:600, letterSpacing:"0.12em", boxSizing:"border-box" }}>
                          PMF NOT RACING
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Signup input — prominent, ABOVE the weather */}
                        {/* Crew counter */}
                        {!isNotRacing && (() => {
                          const n = crewCount(s.date);
                          const lvl = crewLevel(n);
                          const col = crewColors[lvl];
                          const remaining = Math.max(0, 12 - n);
                          if (isNext) {
                            return (
                              <div style={{ margin:"0 0 16px" }}>
                                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.06em", marginBottom:7 }}>
                                  <span style={{ color:"var(--text)" }}>{n} aboard</span>
                                  <span style={{ color: isFull ? "var(--gold-deep, #8B6508)" : "var(--slate)" }}>{isFull ? "full boat" : `${remaining} spot${remaining === 1 ? "" : "s"} left`}</span>
                                </div>
                                <div style={{ height:10, borderRadius:99, background:"var(--surface2)", border:"1px solid var(--border)", overflow:"hidden" }}>
                                  <div style={{ height:"100%", width:`${isFull ? 100 : Math.min(100, Math.round(n / 12 * 100))}%`, background:"var(--gold)", borderRadius:99 }} />
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div style={{ display:"flex", alignItems:"baseline", gap:10, flexWrap:"wrap", justifyContent:"flex-start", marginBottom:10 }}>
                              <span style={{ fontSize:14, fontWeight:800, color: isFull ? "var(--clay, #B0703C)" : "var(--text)" }}>{n} <span style={{ fontWeight:600, color:"var(--slate)" }}>aboard</span>{isFull ? " · full boat" : ""}</span>
                              {!isFull && <span style={{ fontSize:12, color:"var(--slate)" }}>{remaining} spot{remaining === 1 ? "" : "s"} left</span>}
                            </div>
                          );
                        })()}

                        {isFull ? (
                          <div style={{ display:"flex", alignItems:"center", gap:10, width:"100%", maxWidth: isNext ? 520 : "none", margin: (isNext && isMobile) ? "0 auto 16px" : "0 0 16px", padding:14, background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:10, boxSizing:"border-box" }}>
                            <span style={{ fontSize:18 }}>⚓</span>
                            <div>
                              <div style={{ fontWeight:700, fontSize:15, color:"var(--text)" }}>Boat is full for this race</div>
                              <div style={{ fontSize:12, color:"var(--slate)", marginTop:1 }}>Signups are closed. Catch the next one.</div>
                            </div>
                          </div>
                        ) : (
                        <div style={{ display:"flex", flexDirection: isNext ? "row" : "column", gap:8, width:"100%", maxWidth: isNext ? 520 : "none", margin: (isNext && isMobile) ? "0 auto 16px" : "0 0 16px", padding: isNext ? 14 : 0, background: isNext ? "var(--surface2)" : "transparent", borderRadius: isNext ? 10 : 0, border: isNext ? "1px solid var(--border)" : "none", boxSizing:"border-box" }}>
                          <input
                            placeholder={isNext ? "Add your name to this race" : "Your name"}
                            value={signupName[s.date] || ""}
                            onChange={e => setSignupName(n => ({ ...n, [s.date]: e.target.value }))}
                            onKeyDown={e => e.key === "Enter" && requestSignup(s.date)}
                            style={{ flex: isNext ? 1 : "none", width: isNext ? "auto" : "100%", minWidth:0, background:"var(--surface)", border:"1px solid var(--border)", borderRadius:6, padding: isNext ? "12px 14px" : "10px 12px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isNext ? 16 : 15, color:"var(--ink)", outline:"none", boxSizing:"border-box" }}
                          />
                          <button
                            onClick={() => requestSignup(s.date)}
                            style={{ flexShrink:0, width: isNext ? "auto" : "100%", background:"var(--navy)", color:"var(--gold-lt)", border:"none", borderRadius:6, padding: isNext ? "12px 22px" : "11px 16px", fontWeight:700, fontSize: isNext ? 15 : 14, cursor:"pointer", whiteSpace:"nowrap" }}
                          >I'm in</button>
                        </div>
                        )}

                        {/* Detailed weather — only on the expanded next-race tile */}
                        {w && isNext && (
                          <div style={{ background:"var(--surface2)", borderRadius:8, padding:"14px 16px", marginBottom:16 }}>
                            <div style={{ display:"flex", alignItems:"center", justifyContent: isMobile ? "center" : "flex-start", gap:10, marginBottom:12 }}>
                              <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.18em", textTransform:"uppercase", color:"var(--gold-deep, #8B6508)", fontWeight:700 }}>7 PM forecast · Elliott Bay</span>
                              <button
                                onClick={() => fetchWeatherFor(s.date)}
                                disabled={weatherRefreshing[s.date]}
                                title="Refresh forecast"
                                style={{ background:"transparent", border:"none", cursor: weatherRefreshing[s.date] ? "default" : "pointer", color:"var(--slate)", fontSize:14, padding:2, display:"inline-flex", alignItems:"center", animation: weatherRefreshing[s.date] ? "pmf-spin 0.8s linear infinite" : "none" }}
                              >↻</button>
                            </div>
                            <div style={{ display:"grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap:12 }}>
                              <div>
                                <div style={{ height:34, display:"flex", alignItems:"center", justifyContent: isMobile ? "center" : "flex-start" }}><WindIcon /></div>
                                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:20, fontWeight:700, color:"var(--text)" }}>{w.windSpeed} kt</div>
                                <div style={{ fontSize:12, color:"var(--slate)" }}>{w.windCompass} gusts {w.windGust}</div>
                              </div>
                              <div>
                                <div style={{ height:34, display:"flex", alignItems:"center", justifyContent: isMobile ? "center" : "flex-start" }}><CompassIcon /></div>
                                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:20, fontWeight:700, color:"var(--text)" }}>{w.windCompass}</div>
                                <div style={{ fontSize:12, color:"var(--slate)" }}>from {w.windDir}°</div>
                              </div>
                              <div>
                                <div style={{ height:34, display:"flex", alignItems:"center", justifyContent: isMobile ? "center" : "flex-start" }}><ThermometerIcon /></div>
                                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:20, fontWeight:700, color:"var(--text)" }}>{w.temp}°F</div>
                                <div style={{ fontSize:12, color:"var(--slate)" }}>feels {w.feelsLike}°</div>
                              </div>
                              <div>
                                <div style={{ height:34, display:"flex", alignItems:"center", justifyContent: isMobile ? "center" : "flex-start" }}><ConditionIcon code={w.weatherCode} /></div>
                                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:20, fontWeight:700, color:"var(--text)" }}>{w.condition}</div>
                                <div style={{ fontSize:12, color:"var(--slate)" }}>{w.precip}% rain</div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Forecast not loaded yet (in flight, or a transient miss) — keep the
                            section visible with a refresh affordance instead of vanishing. */}
                        {!w && isNext && !isNotRacing && (
                          <div style={{ background:"var(--surface2)", borderRadius:8, padding:"12px 16px", marginBottom:16, display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
                            <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.18em", textTransform:"uppercase", color:"var(--gold-deep, #8B6508)", fontWeight:700 }}>
                              7 PM forecast · {weatherRefreshing[s.date] ? "updating…" : "Elliott Bay"}
                            </span>
                            <button
                              onClick={() => fetchWeatherFor(s.date)}
                              disabled={weatherRefreshing[s.date]}
                              title="Load forecast"
                              style={{ background:"transparent", border:"none", cursor: weatherRefreshing[s.date] ? "default" : "pointer", color:"var(--slate)", fontSize:14, padding:2, display:"inline-flex", alignItems:"center", animation: weatherRefreshing[s.date] ? "pmf-spin 0.8s linear infinite" : "none" }}
                            >↻</button>
                          </div>
                        )}

                        {/* Compact weather — on the smaller "see all" tiles */}
                        {w && !isNext && (
                          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12, padding:"8px 12px", background:"var(--surface2)", borderRadius:8, fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, color:"var(--text)" }}>
                            <span style={{ fontSize:18 }}>💨</span>
                            <span><strong>{w.windSpeed} kt</strong> {w.windCompass}</span>
                            <span style={{ color:"var(--slate)" }}>·</span>
                            <span>{w.temp}°F</span>
                          </div>
                        )}

                        {/* Logistics — only on the next-race tile */}
                        {isNext && (
                          <div style={{ marginBottom:16, padding:"16px", background:"var(--surface2)", borderRadius:8, display:"grid", gap:14 }}>
                            <div>
                              <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--gold-deep, #8B6508)", fontWeight:700, marginBottom:10 }}>When</div>
                              <div style={{ display:"flex", alignItems:"center", gap:isMobile?14:24, flexWrap:"wrap", justifyContent: isMobile ? "center" : "flex-start" }}>
                                <div style={{ textAlign: isMobile ? "center" : "left" }}>
                                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--text)", fontWeight:700 }}>Boat open</div>
                                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:19, fontWeight:700, color:"var(--text)" }}>6:00 PM</div>
                                </div>
                                <div style={{ textAlign: isMobile ? "center" : "left" }}>
                                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--text)", fontWeight:700 }}>Lines off</div>
                                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:19, fontWeight:700, color:"var(--text)" }}>6:20 PM</div>
                                </div>
                                <div style={{ textAlign: isMobile ? "center" : "left" }}>
                                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--text)", fontWeight:700 }}>Race gun</div>
                                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:19, fontWeight:700, color:"var(--text)" }}>7:00 PM</div>
                                </div>
                              </div>
                            </div>
                            <div style={{ borderTop:"1px solid var(--border)", paddingTop:12 }}>
                              <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--gold-deep, #8B6508)", fontWeight:700, marginBottom:8 }}>Where</div>
                              <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", justifyContent: isMobile ? "center" : "flex-start" }}>
                                <span style={{ fontSize:16 }}>📍</span>
                                <a href="https://maps.google.com/?q=Elliott+Bay+Marina,+Seattle" target="_blank" rel="noreferrer" style={{ color:"var(--text)", fontWeight:700, textDecoration:"none", fontSize:15, borderBottom:"1px solid var(--gold)" }}>Elliott Bay Marina</a>
                                <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, color:"var(--slate)" }}>· Gates C & D</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Who's in — live list */}
                        {crew.length > 0 && (
                          <div style={{ marginBottom:14 }}>
                            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.18em", textTransform:"uppercase", color:"var(--gold-deep, #8B6508)", fontWeight:700, marginBottom:8 }}>
                              Who's coming ({crew.length})
                            </div>
                            <div style={{ display:"flex", flexWrap:"wrap", gap:"3px 14px", justifyContent: isMobile ? "center" : "flex-start", fontSize:14, color:"var(--ink-soft)", lineHeight:1.5 }}>
                              {crew.map((c) => {
                                const mine = isMine(s.date, c.name);
                                const isNew = recentlyAdded.includes(c.name);
                                return (
                                  <span key={c.id} style={{ whiteSpace:"nowrap", display:"inline-flex", alignItems:"baseline" }}>
                                    <span style={{ color: isNew ? "var(--gold-deep, #8B6508)" : "var(--ink-soft)", fontWeight: isNew ? 700 : 400 }}>{c.name}</span>
                                    {mine && (
                                      <button
                                        onClick={() => setRemoveConfirm({ id: c.id, date: s.date, name: c.name })}
                                        title="Remove yourself"
                                        style={{ background:"none", border:"none", cursor:"pointer", color:"var(--slate)", fontSize:14, lineHeight:1, padding:"0 0 0 4px", fontWeight:700 }}
                                      >×</button>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* See all upcoming — inside the next-race card */}
                        {isNext && upcoming.length > 1 && (
                          <div style={{ marginTop:18, paddingTop:16, borderTop:"1px solid var(--border)", textAlign: isMobile ? "center" : "left" }}>
                            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.18em", textTransform:"uppercase", color:"var(--gold-deep, #8B6508)", fontWeight:700, marginBottom:10 }}>
                              Can't race this week?
                            </div>
                            <button
                              onClick={() => setShowAllUpcoming(true)}
                              style={{ display:"inline-flex", alignItems:"center", gap:8, background:"var(--surface2)", color:"var(--text)", border:"1px solid var(--gold)", borderRadius:999, padding:"9px 20px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, letterSpacing:"0.1em", textTransform:"uppercase", cursor:"pointer", fontWeight:700 }}
                            >
                              <span style={{ fontSize:11 }}>▼</span>
                              See all {upcoming.length} upcoming races
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Show next only — when expanded */}
            {showAllUpcoming && (
              <button
                onClick={() => setShowAllUpcoming(false)}
                style={{ marginTop:16, display:"inline-flex", alignItems:"center", gap:7, background:"transparent", color:"var(--text)", border:"1px solid var(--border)", borderRadius:999, padding:"7px 16px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.12em", textTransform:"uppercase", cursor:"pointer", fontWeight:600 }}
              >
                <span style={{ display:"inline-block", transform:"rotate(90deg)", fontSize:10 }}>▶</span>
                Show next race only
              </button>
            )}
          </div>
  ) : null;

  return (
    <div style={{ background:"transparent", ...(isMobile ? { height:"100dvh", display:"flex", flexDirection:"column", overflow:"hidden", paddingTop:"env(safe-area-inset-top)" } : { minHeight:"100vh" }) }}>
      {/* Fixed sailing-photo backdrop under a cream veil */}
      <div className="pmf-bg-img" style={{ backgroundImage:`url(${process.env.PUBLIC_URL}/bg.jpg)` }} />
      <div className="pmf-bg-veil" />

      {/* First-open welcome (shows once) */}
      {showWelcome && (
        <div style={{ position:"fixed", inset:0, zIndex:600, background:"rgba(11,22,38,0.55)", overflowY:"auto", padding:"24px 16px", display:"flex" }}>
          <div style={{ maxWidth:440, width:"100%", margin:"auto", background:"var(--bg)", border:"1px solid var(--border)", borderRadius:14, padding: isMobile ? "26px 20px 20px" : "30px 26px 24px", textAlign:"center", boxShadow:"0 12px 50px rgba(11,22,38,0.4)" }}>
            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, letterSpacing:"0.3em", textTransform:"uppercase", color:"var(--gold-lt)", fontWeight:600 }}>Jeanneau Sun Odyssey 45</div>
            <div style={{ fontFamily:"'Caveat',cursive", fontSize:42, color:"var(--text)", lineHeight:1.02, margin:"4px 0 2px" }}>Pardon My French<span style={{ color:"#E04A4A", fontSize:"0.42em", verticalAlign:"super", fontWeight:700, marginLeft:3 }}>Racing</span></div>
            <div style={{ width:60, height:2, background:"var(--gold)", margin:"8px auto 14px" }} />
            <div style={{ fontSize:14, color:"var(--ink)", lineHeight:1.55, marginBottom:18 }}>Welcome! Here are all the things you can do:</div>
            <div style={{ textAlign:"left", display:"grid", gap:2, marginBottom:20 }}>
              {[
                ["⛵","Sign up for a race & view the calendar"],
                ["📸","Relive a race — react & comment"],
                ["🏆","Check the crew leaderboard"],
                ["📊","Geek out on sailing stats"],
                ["⚓","Learn about PMF"],
              ].map(([ic,txt],i,arr) => (
                <div key={txt} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 8px", borderBottom: i < arr.length-1 ? "1px solid var(--surface2)" : "none" }}>
                  <span style={{ fontSize:20, width:26, textAlign:"center", flexShrink:0 }}>{ic}</span>
                  <span style={{ fontSize:14, fontWeight:600, color:"var(--text)" }}>{txt}</span>
                </div>
              ))}
            </div>
            <button onClick={dismissWelcome} style={{ width:"100%", background:"var(--gold)", color:"var(--text)", border:"none", borderRadius:8, padding:14, fontWeight:800, fontSize:16, fontFamily:"'Plus Jakarta Sans',sans-serif", cursor:"pointer" }}>Let's sail →</button>
            <div><button onClick={() => { dismissWelcome(); setShowHelp(true); }} style={{ marginTop:12, background:"none", border:"none", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.1em", textTransform:"uppercase", color:"var(--slate)", textDecoration:"underline", textUnderlineOffset:"3px", cursor:"pointer" }}>or read all the details</button></div>
          </div>
        </div>
      )}

      {/* How-it-works explainer page */}
      {showHelp && (
        <div style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(0,0,0,0.55)", overflowY:"auto", padding:"40px 16px" }} onClick={() => setShowHelp(false)}>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth:680, margin:"0 auto", background:"var(--surface)", borderRadius:12, boxShadow:"0 12px 50px rgba(0,0,0,0.35)", overflow:"hidden" }}>
            {/* Header */}
            <div style={{ background:"var(--navy)", padding:"26px 30px", position:"relative" }}>
              <button onClick={() => setShowHelp(false)} style={{ position:"absolute", top:16, right:18, background:"rgba(255,255,255,0.12)", color:"#fff", border:"none", borderRadius:"50%", width:36, height:36, fontSize:18, cursor:"pointer" }}>✕</button>
              <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, letterSpacing:"0.12em", color:"var(--gold-lt)", textTransform:"uppercase", marginBottom:6 }}>Crew guide</div>
              <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:33, fontWeight:600, color:"#F4F1EA" }}>How this dashboard works</div>
            </div>

            <div style={{ padding:"28px 30px 36px", color:"var(--ink-soft)", fontSize:16, lineHeight:1.7 }}>

              <p style={{ marginBottom:18 }}>Funny story: this began as a dead-simple sign-up sheet — just a way to know who was showing up Thursday night. Then it got a little out of hand. 😅 Now it's the whole digital deck of <strong>Pardon My French</strong>: sign up and see the calendar, relive every race, react and comment (you can even <strong>write a comment with AI</strong> if inspiration runs dry), check the crew leaderboard, and geek out on every number Orca squeezes out of the boat — including the <strong>PMF Index</strong> I built to measure how well we sail our <em>own</em> boat. Four tabs along the bottom — here's the grand tour.</p>

              <h3 style={hStyle}>⛵ Feed — the racing diary</h3>
              <p style={pStyle}>The home tab. Up top is the <strong>next race</strong> — type your name, hit <strong>"I'm in"</strong>, confirm, and you're on the crew list for all to see. Want a later week? Tap <strong>"See all upcoming"</strong>. (Frederick and Jules are always aboard — someone has to drive and someone has to judge.) Below that, every race we've sailed, newest first, each with a story and a strip of gauges.</p>

              <h3 style={hStyle}>🏆 Recap — the season so far</h3>
              <p style={pStyle}>The bird's-eye view: our record at a glance, a running narrative of how the campaign's going, and the full schedule — races sailed, races to come, and the Thursdays we're sitting out. The season in one scroll.</p>

              <h3 style={hStyle}>🧭 Stats — the nerd cabinet</h3>
              <p style={pStyle}>Where the numbers get to show off. A few things worth knowing:</p>
              <ul style={ulStyle}>
                <li style={liStyle}><strong>Performance Index</strong> — our one all-in number, built around the <strong>PMF Index</strong>: instead of measuring ourselves against boats that are simply faster, it measures how well we sailed <em>our own</em> boat versus its potential, then blends in where we finished and how consistent we've been. The boat's GPA. Tap the <strong>?</strong> next to "PMF Index" on any recap for the whole story. Needs a couple of races before it means anything.</li>
                <li style={liStyle}><strong>Performance Rose</strong> — a compass-shaped bloom showing which wind directions we sail best in. Longer, golder petals = we were flying. It fills in beautifully as the season builds.</li>
                <li style={liStyle}><strong>Fleet Context</strong> — the honest mirror. How far behind the winner, the boat just ahead, the boat just behind, and how comfortably we beat the back of the pack — all in time <em>and</em> percent. Plus a "Beat Next" callout naming the rival we keep just missing.</li>
                <li style={liStyle}><strong>Crew Leaderboard</strong> — who's actually shown up and raced (signing up doesn't count — showing up does). Top of the list earns the coveted <strong>🌭 Hotdog</strong>.</li>
                <li style={liStyle}><strong>Records & trends</strong> — bests, averages, and how the finishes are tracking over time.</li>
              </ul>

              <h3 style={hStyle}>🚢 Boat — meet the lady</h3>
              <p style={pStyle}>Everything about <strong>Pardon My French</strong> herself: her story, the key numbers (flip between feet and meters), the sail plan, her speed polar, and the full spec sheet. Plus links to follow her adventures on Instagram and YouTube.</p>

              <h3 style={hStyle}>The race gauges, decoded</h3>
              <p style={pStyle}>Each finished race carries a row of numbers. What they mean and where they're from:</p>
              <ul style={ulStyle}>
                <li style={liStyle}><strong>Max / Avg SOG</strong> — top and average boat speed over the <em>whole evening</em>, by GPS. SOG = "speed over ground." (We trust GPS, not the through-water log — that thing's not calibrated.)</li>
                <li style={liStyle}><strong>Race SOG</strong> — our average pace <em>during the race itself</em> (7:00pm gun to our finish). The honest racing number.</li>
                <li style={liStyle}><strong>Race dist</strong> — how far we actually sailed in that race window, in nautical miles.</li>
                <li style={liStyle}><strong>Avg / Max TWS</strong> — average and peak true wind speed during the race, from the boat's instruments.</li>
                <li style={liStyle}><strong>Avg / Max Polar</strong> — how close we sailed to the boat's theoretical best (100% = nailing the polar target). Read off Orca. Fair warning: the polar model still needs calibration, so take these with a grain of sea salt for now.</li>
                <li style={liStyle}><strong>Avg TWD</strong> — the prevailing true wind direction during the race, in degrees.</li>
              </ul>

              <h3 style={hStyle}>How Race SOG is computed</h3>
              <p style={pStyle}>Orca logs the boat's GPS position every minute, all evening. To isolate the <em>race</em>, we grab the track from <strong>7:00pm</strong> (the gun) to our <strong>finish time</strong> (off the official results), add up the distance between each point, and divide by the race duration. Clean race-only speed — no credit for motoring out and back.</p>

              <h3 style={hStyle}>The fleet-context numbers</h3>
              <p style={pStyle}>When Frederick logs a race, he also notes the boats finishing around us — the winner, the median boat, the back-marker, and whoever was just ahead and just behind. The AI reads them straight off the results screenshot. That's what powers the Fleet Context module in Stats, so we can see exactly who to chase next.</p>

              <h3 style={hStyle}>The weather forecast</h3>
              <p style={pStyle}>Wind and temperature for upcoming races is a <strong>forecast for Elliott Bay at 6pm</strong>, pulled live from Open-Meteo. It's a model's best guess, not a measurement — a heads-up, not gospel. Reloads with the app, or tap <strong>↻</strong> to refresh on demand.</p>

              <h3 style={hStyle}>React, comment, share photos</h3>
              <p style={pStyle}>Under each race, tap a <strong>reaction</strong> (👏 Kudos, 🚀 Send it, 😬 Yikes, 🏆 Podium) or leave a <strong>comment</strong> — type your name once and it sticks for the session. Stuck for words? Hit <strong>✨ Write with AI</strong> and it'll draft something you can tweak before posting. Got photos? Tap <strong>"📷 Add your photos"</strong> on any race card to drop them in. Everyone sees it all, live.</p>

              <h3 style={hStyle}>Where it all comes from</h3>
              <p style={pStyle}>Finish places and times: the <strong>official Downtown Sailing Series results</strong>. Boat performance: <strong>Orca</strong>, our onboard nav app. Frederick enters each race after the fact, and the page updates for everyone in an instant. Spot a bug or have an idea? The <strong>⋯ menu</strong> up top has a Feedback button.</p>

              <div style={{ marginTop:24, padding:"14px 16px", background:"var(--surface2)", borderRadius:8, fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, color:"var(--slate)" }}>
                ⛵ Boat open from <strong style={{ color:"var(--text)" }}>6:00pm</strong>, lines off at <strong style={{ color:"var(--text)" }}>6:20pm</strong>. Race gun at <strong style={{ color:"var(--text)" }}>7:00pm</strong>. Home base: Elliott Bay Marina, Gates C & D.
              </div>

              <button onClick={() => setShowHelp(false)} style={{ marginTop:24, width:"100%", background:"var(--navy)", color:"#F4F1EA", border:"none", borderRadius:8, padding:"13px", fontWeight:700, fontSize:15, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Got it — back to racing</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Photo lightbox ── */}
      {lightbox && (
        <Lightbox
          photos={lightbox.photos}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onIndex={(i) => setLightbox(l => ({ ...l, index: i }))}
        />
      )}

      {perfHelp && (
        <div onClick={()=>setPerfHelp(false)} style={{ position:"fixed", inset:0, zIndex:500, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:"var(--surface)", borderRadius:12, maxWidth:380, width:"100%", padding:"24px 26px", boxShadow:"0 12px 50px rgba(0,0,0,0.4)" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
              <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:19, fontWeight:700, color:"var(--text)" }}>What is the PMF Index?</div>
              <button onClick={()=>setPerfHelp(false)} style={{ background:"none", border:"none", fontSize:22, color:"var(--slate)", cursor:"pointer", lineHeight:1, padding:0 }}>×</button>
            </div>
            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:14, color:"var(--ink-soft, #33404d)", lineHeight:1.6 }}>
              I built this because comparing ourselves to boats that are simply <b style={{ color:"var(--text)" }}>faster</b> than us never told the real story. I wanted a number that shows how well we sail <b style={{ color:"var(--text)" }}>our own boat</b> — far more honest about how we actually did.
              <div style={{ marginTop:10 }}>
                It's an approximation, sure. But it feels right: when I look at the number, talk it over with the crew, and weigh how the night went, it lines up. — <b style={{ color:"var(--text)" }}>Frederick</b>
              </div>
              <div style={{ marginTop:12, fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, color:"var(--slate)", lineHeight:1.55 }}>
                Under the hood: our average race speed vs. the boat's all-around polar target for that night's wind. 100% = right at the boat's potential; over 100% = we beat it (often on puffy nights).
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Remove comment confirmation ── */}
      {removeCommentConfirm && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }} onClick={() => setRemoveCommentConfirm(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background:"var(--surface)", borderRadius:12, padding:"26px 24px", maxWidth:340, width:"100%", textAlign:"center", boxShadow:"0 8px 40px rgba(0,0,0,0.25)" }}>
            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:19, fontWeight:700, color:"var(--text)", marginBottom:18 }}>
              Delete your comment?
            </div>
            <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
              <button onClick={() => setRemoveCommentConfirm(null)} style={{ background:"transparent", color:"var(--slate)", border:"1px solid var(--border)", borderRadius:6, padding:"11px 20px", fontWeight:600, fontSize:14, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Keep it</button>
              <button
                onClick={async () => {
                  const { id } = removeCommentConfirm;
                  setRemoveCommentConfirm(null);
                  try { await onRemoveComment(id); forgetMyComment(id); } catch (e) {}
                }}
                style={{ background:"#C0392B", color:"#fff", border:"none", borderRadius:6, padding:"11px 22px", fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}
              >Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Remove signup confirmation ── */}
      {removeConfirm && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }} onClick={() => setRemoveConfirm(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background:"var(--surface)", borderRadius:12, padding:"26px 24px", maxWidth:360, width:"100%", textAlign:"center", boxShadow:"0 8px 40px rgba(0,0,0,0.25)" }}>
            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:20, fontWeight:700, color:"var(--text)", marginBottom:8 }}>
              Remove {removeConfirm.name}?
            </div>
            <div style={{ fontSize:14, color:"var(--slate)", marginBottom:20 }}>
              You'll be taken off the list for {fmtDateLong(removeConfirm.date)}.
            </div>
            <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
              <button onClick={() => setRemoveConfirm(null)} style={{ background:"transparent", color:"var(--slate)", border:"1px solid var(--border)", borderRadius:6, padding:"11px 20px", fontWeight:600, fontSize:14, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Keep me in</button>
              <button
                onClick={async () => {
                  const { id, date, name } = removeConfirm;
                  setRemoveConfirm(null);
                  try { await onRemoveSignup(id); forgetMySignup(date, name); } catch (e) {}
                }}
                style={{ background:"#C0392B", color:"#fff", border:"none", borderRadius:6, padding:"11px 22px", fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}
              >Remove me</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Feedback modal ── */}
      {showFeedback && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }} onClick={() => setShowFeedback(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background:"var(--surface)", borderRadius:12, padding:"26px 24px", maxWidth:440, width:"100%", boxShadow:"0 8px 40px rgba(0,0,0,0.25)" }}>
            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:22, fontWeight:800, color:"var(--text)", marginBottom:6 }}>Send feedback</div>
            <div style={{ fontSize:14, color:"var(--slate)", marginBottom:18 }}>Ideas, bugs, or requests for the app — goes straight to Frederick.</div>
            <input placeholder="Your name" value={fbName} onChange={e => setFbName(e.target.value)} style={fbInput} />
            <input placeholder="Your email" type="email" value={fbEmail} onChange={e => setFbEmail(e.target.value)} style={fbInput} />
            <textarea placeholder="Your feedback…" rows={4} value={fbText} onChange={e => setFbText(e.target.value)} style={{ ...fbInput, resize:"vertical", lineHeight:1.5 }} />
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:8 }}>
              <button onClick={() => setShowFeedback(false)} style={{ background:"transparent", color:"var(--slate)", border:"1px solid var(--border)", borderRadius:6, padding:"11px 20px", fontWeight:600, fontSize:14, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
              <button
                onClick={() => {
                  if (!fbText.trim()) return;
                  const subject = encodeURIComponent("Racing app feedback");
                  const body = encodeURIComponent(`From: ${fbName || "(no name)"} <${fbEmail || "no email"}>\n\n${fbText}`);
                  window.location.href = `mailto:frederick@sailingpardonmyfrench.com?subject=${subject}&body=${body}`;
                  setShowFeedback(false); setFbName(""); setFbEmail(""); setFbText("");
                }}
                style={{ background: btnGradient, color:"var(--gold-lt)", border:"none", borderRadius:6, padding:"11px 26px", fontWeight:700, fontSize:15, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}
              >Send</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Comment modal ── */}
      {commentModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }} onClick={() => setCommentModal(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background:"var(--surface)", borderRadius:12, padding:"26px 24px", maxWidth:420, width:"100%", boxShadow:"0 8px 40px rgba(0,0,0,0.25)" }}>
            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:26, fontWeight:600, color:"var(--text)", marginBottom:18, display:"flex", alignItems:"center", gap:9 }}>
              <AiSparkle size={22} />
              Add a comment
            </div>
            <input
              autoFocus={!savedName}
              placeholder="Your name"
              value={cmName}
              onChange={e => setCmName(e.target.value)}
              style={{ width:"100%", background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:8, padding:"11px 13px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:15, color:"var(--ink)", outline:"none", marginBottom:10 }}
            />
            <textarea
              autoFocus={!!savedName}
              placeholder="Say something…"
              rows={3}
              value={cmText}
              onChange={e => setCmText(e.target.value)}
              onKeyDown={async e => {
                if (e.key === "Enter" && !e.shiftKey && cmName.trim() && cmText.trim()) {
                  e.preventDefault();
                  const created = await onAddComment(commentModal.date, cmName.trim(), cmText.trim());
                  if (created?.id) rememberMyComment(created.id);
                  setSavedName(cmName.trim());
                  setCommentModal(null);
                }
              }}
              style={{ width:"100%", background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:8, padding:"11px 13px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:15, color:"var(--ink)", outline:"none", resize:"vertical", lineHeight:1.5, marginBottom:10 }}
            />

            {/* Write with AI */}
            <div style={{ marginBottom:18 }}>
              <div style={{ display:"flex", alignItems:"center", gap:7, fontSize:12, color: aiLimitMsg ? "#C0392B" : "var(--slate)", marginBottom:8 }}>
                <AiSparkle size={14} color={aiLimitMsg ? "#C0392B" : "var(--gold-deep, #8B6508)"} />
                {aiLimitMsg
                  ? "Sorry, limited to 2 generations."
                  : "Not inspired? Type a few words and PMF will do the rest."}
              </div>
              <button
                onClick={generateAiComment}
                disabled={aiGenLoading || !cmText.trim()}
                style={{ display:"inline-flex", alignItems:"center", gap:7, background:"transparent", color: cmText.trim() ? "var(--gold-deep, #8B6508)" : "var(--slate)", border:"1px solid var(--border)", borderRadius:999, padding:"8px 16px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:700, cursor: (aiGenLoading || !cmText.trim()) ? "default" : "pointer", opacity: (aiGenLoading || !cmText.trim()) ? 0.55 : 1 }}
              >
                <AiSparkle size={14} color={cmText.trim() ? "var(--gold-deep, #8B6508)" : "var(--slate)"} />
                {aiGenLoading ? "Writing…" : "Write with AI"}
              </button>
            </div>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={() => setCommentModal(null)} style={{ background:"transparent", color:"var(--slate)", border:"1px solid var(--border)", borderRadius:6, padding:"11px 20px", fontWeight:600, fontSize:14, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
              <button
                onClick={async () => {
                  if (cmName.trim() && cmText.trim()) {
                    const created = await onAddComment(commentModal.date, cmName.trim(), cmText.trim());
                    if (created?.id) rememberMyComment(created.id);
                    setSavedName(cmName.trim());
                    setCommentModal(null);
                  }
                }}
                style={{ background: btnGradient, color:"var(--gold-lt)", border:"none", borderRadius:6, padding:"11px 26px", fontWeight:700, fontSize:15, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}
              >Post</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reaction name prompt ── */}
      {reactPrompt && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }} onClick={() => { setReactPrompt(null); setReactNameInput(""); }}>
          <div onClick={e => e.stopPropagation()} style={{ background:"var(--surface)", borderRadius:12, padding:"28px 26px", maxWidth:340, width:"100%", textAlign:"center", boxShadow:"0 8px 40px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize:40, marginBottom:10 }}>{reactPrompt.emoji}</div>
            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:22, fontWeight:600, color:"var(--text)", marginBottom:16 }}>
              React as…
            </div>
            <input
              autoFocus
              placeholder="Your name"
              value={reactNameInput}
              onChange={e => setReactNameInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && reactNameInput.trim()) {
                  onToggleReaction(reactPrompt.date, reactPrompt.emoji, reactNameInput.trim(), null);
                  setSavedName(reactNameInput.trim());
                  setReactPrompt(null); setReactNameInput("");
                }
              }}
              style={{ width:"100%", textAlign:"center", fontSize:16, padding:"11px", borderRadius:8, border:"1px solid var(--border)", background:"var(--surface2)", outline:"none", marginBottom:18, fontFamily:"'Plus Jakarta Sans',sans-serif" }}
            />
            <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
              <button
                onClick={() => {
                  if (reactNameInput.trim()) {
                    onToggleReaction(reactPrompt.date, reactPrompt.emoji, reactNameInput.trim(), null);
                    setSavedName(reactNameInput.trim());
                    setReactPrompt(null); setReactNameInput("");
                  }
                }}
                style={{ background: btnGradient, color:"var(--gold-lt)", border:"none", borderRadius:6, padding:"11px 24px", fontWeight:700, fontSize:15, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}
              >React</button>
              <button onClick={() => { setReactPrompt(null); setReactNameInput(""); }} style={{ background:"transparent", color:"var(--slate)", border:"1px solid var(--border)", borderRadius:6, padding:"11px 20px", fontWeight:600, fontSize:14, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmation popup ── */}
      {confirmState && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
          <div style={{ background:"var(--surface)", borderRadius:12, padding:"32px 28px", maxWidth:380, width:"100%", boxShadow:"0 8px 40px rgba(0,0,0,0.2)", textAlign:"center" }}>
            <div style={{ fontSize:36, marginBottom:12 }}>⛵</div>
            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:26, fontWeight:600, color:"var(--text)", marginBottom:8 }}>
              You're signing up as
            </div>
            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:18, fontWeight:600, color:"var(--gold)", marginBottom:6 }}>
              {confirmState.name}
            </div>
            <div style={{ fontSize:15, color:"var(--slate)", marginBottom:24 }}>
              for Race {SCHEDULE.find(s => s.date === confirmState.date)?.raceNo} · {fmtDateLong(confirmState.date)}
            </div>
            <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
              <button
                onClick={confirmSignup}
                disabled={confirmLoading}
                style={{ background:"var(--navy)", color:"#F4F1EA", border:"none", borderRadius:6, padding:"11px 24px", fontWeight:700, fontSize:16, cursor: confirmLoading ? "default" : "pointer", opacity: confirmLoading ? 0.7 : 1, fontFamily:"'Plus Jakarta Sans',sans-serif" }}
              >{confirmLoading ? "Saving…" : "Yes, I'm in!"}</button>
              <button
                onClick={() => setConfirmState(null)}
                style={{ background:"transparent", color:"var(--slate)", border:"1px solid var(--border)", borderRadius:6, padding:"11px 20px", fontWeight:600, fontSize:15, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}
              >Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Scroll area (mobile app-shell: this scrolls, nav stays put) */}
      <div data-pmf-scroll style={isMobile ? { flex:1, overflowY:"auto", WebkitOverflowScrolling:"touch", minHeight:0 } : {}}>
      {/* Mobile masthead — shared PmfMasthead lockup (scrolls away) */}
      {isMobile && (
        <PmfMasthead
          isMobile
          subtitle="Downtown Sailing Series 2026 · #2590"
          cornerSlot={
            <div style={{ position:"relative" }}>
              <button
                onClick={() => setShowDeskMenu(v => !v)}
                aria-label="Menu"
                style={{ background:"none", border:"none", cursor:"pointer", color:"var(--slate)", fontSize:26, fontWeight:400, lineHeight:1, padding:"2px 4px", marginRight:2 }}
              >⋯</button>
              {showDeskMenu && (
                <>
                  <div onClick={() => setShowDeskMenu(false)} style={{ position:"fixed", inset:0, zIndex:39 }} />
                  <div style={{ position:"absolute", top:34, right:0, zIndex:41, background:"var(--surface)", border:"1px solid var(--border)", borderRadius:10, boxShadow:"0 8px 30px rgba(0,0,0,0.18)", minWidth:200, overflow:"hidden", textAlign:"left" }}>
                    <button onClick={() => navTo("recap","season-table")} style={deskMenuItem}>Race calendar</button>
                    <button onClick={() => navTo("recap","crew-leaderboard")} style={deskMenuItem}>Crew leaderboard</button>
                    <button onClick={() => navTo("recap","completed-races")} style={deskMenuItem}>Season standing</button>
                    <button onClick={() => navTo("boat",null)} style={deskMenuItem}>About PMF</button>
                    <button onClick={() => { setShowDeskMenu(false); setShowHelp(true); }} style={deskMenuItem}>About the app</button>
                    <button onClick={() => { setShowDeskMenu(false); setShowFeedback(true); }} style={deskMenuItem}>Feedback</button>
                    {onEnterSkipper && (
                      <button onClick={() => { setShowDeskMenu(false); onEnterSkipper(); }} style={{ ...deskMenuItem, borderBottom:"none" }}>Skipper login</button>
                    )}
                  </div>
                </>
              )}
            </div>
          }
        />
      )}
      {/* Desktop masthead — shared PmfMasthead lockup (scrolls) + sticky tabs */}
      {!isMobile && (
      <>
      <PmfMasthead
        subtitle="Downtown Sailing Series 2026 · #2590"
        cornerSlot={
          <div style={{ position:"relative" }}>
            <button
              onClick={() => setShowDeskMenu(v => !v)}
              aria-label="Menu"
              style={{ background:"none", border:"none", color: showDeskMenu ? "var(--text)" : "var(--slate)", cursor:"pointer", fontSize:24, fontWeight:400, lineHeight:1, padding:"2px 0 2px 8px" }}
            >⋯</button>
            {showDeskMenu && (
              <>
                <div onClick={() => setShowDeskMenu(false)} style={{ position:"fixed", inset:0, zIndex:39 }} />
                <div style={{ position:"absolute", top:38, right:0, zIndex:41, background:"var(--surface)", border:"1px solid var(--border)", borderRadius:10, boxShadow:"0 8px 30px rgba(0,0,0,0.18)", minWidth:190, overflow:"hidden", textAlign:"left" }}>
                  <button onClick={() => navTo("recap","season-table")} style={deskMenuItem}>Race calendar</button>
                  <button onClick={() => navTo("recap","crew-leaderboard")} style={deskMenuItem}>Crew leaderboard</button>
                  <button onClick={() => navTo("recap","completed-races")} style={deskMenuItem}>Season standing</button>
                  <button onClick={() => navTo("boat",null)} style={deskMenuItem}>About PMF</button>
                  <button onClick={() => { setShowDeskMenu(false); setShowHelp(true); }} style={deskMenuItem}>About the app</button>
                  <button onClick={() => { setShowDeskMenu(false); setShowFeedback(true); }} style={deskMenuItem}>Feedback</button>
                  {onEnterSkipper && (
                    <button onClick={() => { setShowDeskMenu(false); onEnterSkipper(); }} style={{ ...deskMenuItem, borderBottom:"none" }}>Skipper login</button>
                  )}
                </div>
              </>
            )}
          </div>
        }
      />

      {/* Sticky tabs — full-width equal slots, pin to top when masthead scrolls away */}
      <nav style={{ background:"var(--bg)", position:"sticky", top:0, zIndex:30 }}>
        <div style={{ maxWidth:900, margin:"0 auto", padding:"0 24px" }}>
          <div style={{ display:"flex", borderBottom:"1px solid var(--border)" }}>
            {[
              { id:"feed",  label:"Feed" },
              { id:"recap", label:"Season" },
              { id:"stats", label:"Stats" },
              { id:"boat",  label:"Boat" },
            ].map(t => (
              <button key={t.id} onClick={() => goTab(t.id)} style={{
                flex:1, display:"flex", alignItems:"center", justifyContent:"center",
                padding:"17px 2px", background:"none", border:"none", cursor:"pointer",
                fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:15, letterSpacing:"0.12em", textTransform:"uppercase",
                fontWeight: activeTab===t.id ? 700 : 600,
                color: activeTab===t.id ? "var(--text)" : "var(--slate)",
                borderBottom: activeTab===t.id ? "2px solid var(--gold-lt)" : "2px solid transparent",
              }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </nav>
      </>
      )}

      {/* Skipper announcement banner — shows until the referenced race day has passed */}
      {(() => {
        if (!announcement || !announcement.message) return null;
        // Expire after the race day passes (end of that day)
        if (announcement.race_date) {
          const raceEnd = new Date(announcement.race_date + "T23:59:59");
          if (new Date() > raceEnd) return null;
        }
        return (
          <div style={{ maxWidth:900, margin:"0 auto", padding: isMobile ? "14px 14px 0" : "16px 24px 0", boxSizing:"border-box" }}>
            <div style={{ background:"var(--surface)", border:"3px solid #C0392B", borderRadius:10, padding:"14px 16px", display:"flex", alignItems:"center", gap:14, boxShadow:"0 2px 12px rgba(192,57,43,0.18)", boxSizing:"border-box" }}>
              <div style={{ width:40, height:40, borderRadius:"50%", background:"#C0392B", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>📣</div>
              <div style={{ minWidth:0, flex:1 }}>
                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase", color:"#C0392B", fontWeight:700, marginBottom:2 }}>Skipper's note</div>
                <div style={{ fontWeight:700, color:"var(--text)", fontSize:15, lineHeight:1.4, overflowWrap:"break-word", wordBreak:"break-word" }}>{announcement.message}</div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Desktop top nav ── */}
      <div style={{ maxWidth:900, margin:"0 auto", padding: isMobile ? "0 14px 30px" : "0 24px 80px" }}>

        {/* ════════════ SEASON RECAP tab (stub) ════════════ */}
        {activeTab === "recap" && (
          <div style={{ paddingTop: isMobile ? 18 : 28 }}>
            {racesSailed === 0 ? (
              <div style={{ padding: isMobile ? "40px 0" : "60px 0", textAlign:"center" }}>
                <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:22, fontWeight:700, color:"var(--text)", marginBottom:8 }}>Season Recap</div>
                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, letterSpacing:"0.1em", textTransform:"uppercase", color:"var(--slate)" }}>No races sailed yet</div>
              </div>
            ) : (<>
              {/* Record cards */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap: isMobile ? 8 : 14, marginBottom: isMobile ? 18 : 24 }}>
                <div style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:12, padding: isMobile ? "16px 10px" : "20px 16px", textAlign:"center" }}>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isMobile ? 30 : 38, fontWeight:800, color:"var(--text)", lineHeight:1 }}>{racesSailed}</div>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:9, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--gold-deep, #8B6508)", fontWeight:700, marginTop:6 }}>Races sailed</div>
                </div>
                <div style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:12, padding: isMobile ? "16px 10px" : "20px 16px", textAlign:"center" }}>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isMobile ? 30 : 38, fontWeight:800, color:"var(--text)", lineHeight:1 }}>{bestPlace}<span style={{ fontSize: isMobile ? 14 : 18, color:"var(--slate)" }}>{ordinalSuffix(bestPlace)}</span></div>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:9, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--gold-deep, #8B6508)", fontWeight:700, marginTop:6 }}>Best finish</div>
                </div>
                <div style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:12, padding: isMobile ? "16px 10px" : "20px 16px", textAlign:"center" }}>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isMobile ? 30 : 38, fontWeight:800, color:"var(--text)", lineHeight:1 }}>{avgPlace}</div>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:9, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--gold-deep, #8B6508)", fontWeight:700, marginTop:6 }}>Avg place</div>
                </div>
              </div>

              {/* Completed races — own section, above the leaderboard */}
              {completedSection}

              {/* Upcoming races calendar — moved up, right after completed races */}
              {upcomingSection}

              {/* Crew leaderboard — podium */}
              {crewBoard.length > 0 && (
                <div id="crew-leaderboard" style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:10, padding: isMobile ? "18px 16px" : "22px 24px", marginBottom: isMobile ? 24 : 32, boxShadow:"0 2px 14px rgba(0,0,0,0.07)" }}>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--gold-deep, #8B6508)", fontWeight:700, marginBottom:18 }}>🏆 Crew leaderboard</div>
                  {/* podium: silver · gold · bronze */}
                  <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"center", gap: isMobile ? 8 : 14, marginBottom: crewBoard.length > 3 ? 16 : 0 }}>
                    {[1,0,2].map(slot => {
                      const entry = crewBoard[slot];
                      if (!entry) return <div key={slot} style={{ flex:1, maxWidth:118 }} />;
                      const [name, count] = entry;
                      const h = [104,80,62][slot];
                      const dog = isMobile ? [30,23,18][slot] : [36,27,21][slot];
                      return (
                        <div key={slot} style={{ flex:1, maxWidth:118, textAlign:"center", minWidth:0 }}>
                          <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:700, fontSize: isMobile ? 13 : 14, color:"var(--text)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", marginBottom:8 }}>
                            {name}
                          </div>
                          <div style={{ height:h, borderRadius:"10px 10px 0 0", background:btnGradient, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:4 }}>
                            <span style={{ fontSize:dog, lineHeight:1 }}>🌭</span>
                            <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isMobile ? 12 : 13, fontWeight:700, color:"var(--gold-lt)" }}>{count}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* ranks 4–8 */}
                  {crewBoard.slice(3).map(([name, count], i, arr) => (
                    <div key={name} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 0", borderBottom: i < arr.length - 1 ? "1px solid var(--surface2)" : "none" }}>
                      <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, color:"var(--slate)", width:18 }}>{i + 4}</span>
                      <span style={{ flex:1, fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:600, fontSize:14, color:"var(--text)", minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{name}</span>
                      <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, color:"var(--slate)" }}>{count} race{count !== 1 ? "s" : ""}</span>
                    </div>
                  ))}
                </div>
              )}

            </>)}
          </div>
        )}

        {/* ════════════ STATS tab (stub) ════════════ */}
        {activeTab === "stats" && (
          <div style={{ paddingTop: isMobile ? 18 : 28 }}>
            <Suspense fallback={<div style={{ padding: 48, textAlign: "center", color: "var(--slate)", fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 13, letterSpacing: "0.08em" }}>Loading…</div>}>
              <StatsTab races={races} signups={signups} isMobile={isMobile} />
            </Suspense>
          </div>
        )}

        {activeTab === "boat" && (
          <div style={{ paddingTop: isMobile ? 18 : 28 }}>
            <Suspense fallback={<div style={{ padding: 48, textAlign: "center", color: "var(--slate)", fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 13, letterSpacing: "0.08em" }}>Loading…</div>}>
              <BoatTab isMobile={isMobile} boatInfo={boatInfo} />
            </Suspense>
          </div>
        )}

        {/* ════════════ RACING FEED tab ════════════ */}
        {activeTab === "feed" && <>

        {/* ── Upcoming races — signup ── */}
        {nextOnTop && nextRaceBlock}


        {/* Race cards */}
        <div style={{ marginTop: isMobile ? 24 : 38 }}>
        {sorted.map((r, i) => (
          <React.Fragment key={r.id}><article id={`race-${r.raceNo}`} style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:10, marginBottom: isMobile ? 24 : 38, overflow:"hidden", boxShadow:"0 2px 14px rgba(0,0,0,0.07)" }}>
            <div style={{ background:"linear-gradient(135deg, var(--navy) 0%, #2E5A83 55%, #4B7BA7 100%)", padding: isMobile ? "16px 18px" : "20px 28px", position:"relative" }}>
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12 }}>
                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.2em", textTransform:"uppercase", fontWeight:700, color:"var(--gold-lt)", lineHeight:1.5 }}>
                  Race {r.raceNo}
                  {!r.didNotRace && r.raceClass && <><span style={{ color:"rgba(255,255,255,0.35)", margin:"0 7px" }}>·</span>{r.raceClass}</>}
                  <span style={{ color:"rgba(255,255,255,0.35)", margin:"0 7px" }}>·</span>
                  <span style={{ color:"rgba(255,255,255,0.72)" }}>{fmtDateLong(r.date)}</span>
                  {r.didNotRace && <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#fff", background:"#C0392B", borderRadius:6, padding:"3px 9px", marginLeft:8 }}>PMF sat out</span>}
                </div>
                <button
                  onClick={() => shareRace(r)}
                  aria-label="Share this recap"
                  title="Share this recap"
                  style={{ flexShrink:0, width:34, height:34, borderRadius:999, background:"rgba(255,255,255,0.12)", border:"1px solid rgba(255,255,255,0.25)", color:"#FFF7E6", display:"inline-flex", alignItems:"center", justifyContent:"center", cursor:"pointer", padding:0 }}
                >
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3v12" /><path d="M8 7l4-4 4 4" /><path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
                  </svg>
                </button>
              </div>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:600, fontSize:"clamp(28px,5vw,38px)", lineHeight:1.15, margin:"10px 0 0", color:"#F4F1EA" }}>{r.title}</h2>
            </div>
            {r.didNotRace && (
              <div style={{ padding: isMobile ? "16px 18px" : "18px 28px", background:"var(--surface2)", borderTop:"1px solid var(--border)", borderBottom:"1px solid var(--border)", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, color:"var(--slate)", lineHeight:1.6, textAlign:"center" }}>
                ⚓ PMF didn't sail this race. Fleet results are logged below to keep the competition tracking current.
              </div>
            )}
            {/* Result — full-width band directly under the header */}
            {(typeof r.place === "number" || r.winner) && (
              <div style={{ background:"var(--surface)", borderTop:"1px solid var(--border)", padding: isMobile ? "18px 18px" : "20px 28px", textAlign:"center" }}>
                <div style={{ display:"flex", justifyContent:"center", alignItems:"flex-start", gap: isMobile ? 28 : 48, flexWrap:"nowrap" }}>
                  {typeof r.place === "number" && (
                    <div>
                      <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isMobile ? 30 : 40, fontWeight:800, color:"var(--gold)", lineHeight:1, whiteSpace:"nowrap" }}>
                        {r.place}<span style={{ fontSize: isMobile ? 14 : 16, fontWeight:600, color:"var(--slate)" }}> of {r.fleet}</span>
                      </div>
                      <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--gold-deep, #8B6508)", fontWeight:700, marginTop:6 }}>Finish</div>
                    </div>
                  )}
                  {r.time && (
                    <div>
                      <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isMobile ? 30 : 40, fontWeight:800, color:"var(--text)", lineHeight:1, whiteSpace:"nowrap" }}>{r.time}</div>
                      <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--slate)", fontWeight:700, marginTop:6 }}>Our time</div>
                    </div>
                  )}
                  {(() => {
                    const perf = pmfPerf(r);
                    if (perf == null) return null;
                    return (
                      <div>
                        <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isMobile ? 30 : 40, fontWeight:800, color: perf >= 100 ? "var(--gold)" : "var(--text)", lineHeight:1, whiteSpace:"nowrap" }}>{perf}<span style={{ fontSize: isMobile ? 16 : 20, fontWeight:600, color:"var(--slate)" }}>%</span></div>
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:5, marginTop:6 }}>
                          <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--slate)", fontWeight:700 }}>PMF Index</span>
                          <button onClick={()=>setPerfHelp(true)} aria-label="What is the PMF Index?" style={{ width:16, height:16, borderRadius:99, border:"1px solid var(--border)", background:"none", color:"var(--slate)", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, fontWeight:700, cursor:"pointer", lineHeight:1, padding:0, display:"inline-flex", alignItems:"center", justifyContent:"center" }}>?</button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                {r.winner && (
                  <div style={{ maxWidth:560, margin:"14px auto 0", paddingTop:12, borderTop:"1px solid var(--surface2)", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:15, color:"var(--slate)" }}>
                    <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, letterSpacing:"0.1em", textTransform:"uppercase" }}>1st</span> <strong style={{ color:"var(--text)", fontWeight:700 }}>{r.winner}</strong>
                  </div>
                )}
              </div>
            )}
            {!r.didNotRace && (() => {
              const raceSog = computeRaceSog(r.distance, r.time);
              const twdMain = mainAngle(r.twd);
              // Flat metric list (used for mobile)
              const flat = [
                { lbl:"Max SOG", val: stat(r.maxSog, " kt"), unit:"" },
                { lbl:"Avg SOG", val: stat(r.avgSog, " kt"), unit:"" },
                { lbl:"Race SOG", val: raceSog || "—", unit: raceSog ? " kt" : "" },
                { lbl:"Race dist", val: r.distance ? r.distance : "—", unit: r.distance ? " nm" : "" },
                { lbl:"Avg TWS", val: stat(r.avgTws, " kt"), unit:"" },
                { lbl:"Max TWS", val: stat(r.maxTws, " kt"), unit:"" },
                { lbl:"Avg Polar", val: r.avgPolar ? r.avgPolar : "—", unit: r.avgPolar ? "%" : "" },
                { lbl:"Max Polar", val: r.maxPolar ? r.maxPolar : "—", unit: r.maxPolar ? "%" : "" },
                { lbl:"Avg TWD", val: twdMain || "—", unit:"" },
              ];
              const mobileTemp = raceTemps[r.date];

              if (isMobile) {
                // Clean 2-col stat list
                return (
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:1, background:"var(--border)", borderTop:"1px solid var(--border)", borderBottom:"1px solid var(--border)", marginTop:0 }}>
                    {[...flat, { lbl:"Temp", val: (mobileTemp!==undefined&&mobileTemp!==null)?mobileTemp:"—", unit:(mobileTemp!==undefined&&mobileTemp!==null)?"°F":"" }].map((g, i) => (
                      <div key={i} style={{ background:"var(--surface2)", display:"flex", alignItems:"center", justifyContent:"space-between", padding:"11px 14px" }}>
                        <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.1em", color:"var(--slate)", textTransform:"uppercase" }}>{g.lbl}</span>
                        <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:16, fontWeight:700, color:"var(--text)" }}>{g.val}<span style={{ fontSize:11, color:"var(--slate)" }}>{g.unit}</span></span>
                      </div>
                    ))}
                  </div>
                );
              }

              // Desktop: 5 stacked boxes
              const temp = raceTemps[r.date];
              const boxes = [
                { metrics: [ flat[0], flat[1] ] },                          // Max SOG / Avg SOG
                { metrics: [ flat[2], flat[3] ] },                          // Race SOG / Race dist
                { metrics: [ flat[5], flat[4] ] },                          // Max TWS / Avg TWS
                { metrics: [ flat[7], flat[6] ] },                          // Max Polar / Avg Polar
                { metrics: [ flat[8], { lbl:"Temp", val: (temp!==undefined&&temp!==null)?temp:"—", unit:(temp!==undefined&&temp!==null)?"°F":"" } ] }, // Avg TWD / Temp
              ];
              return (
                <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:1, background:"var(--border)", borderTop:"1px solid var(--border)", borderBottom:"1px solid var(--border)", marginTop:0 }}>
                  {boxes.map((box, bi) => (
                    <div key={bi} style={{ background:"var(--surface2)", display:"flex", flexDirection:"column", justifyContent: box.center ? "center" : "flex-start" }}>
                      {box.metrics.map((g, mi) => (
                        <div key={mi} style={{ padding:"10px 6px", textAlign:"center", borderTop: mi > 0 ? "1px solid var(--border)" : "none", flex: box.center ? "none" : "1" }}>
                          <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:9, letterSpacing:"0.14em", color:"var(--slate)", textTransform:"uppercase" }}>{g.lbl}</div>
                          <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:20, fontWeight:700, color:"var(--text)", marginTop:2 }}>{g.val}<span style={{ fontSize:11, color:"var(--slate)" }}>{g.unit}</span></div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              );
            })()}
            <div style={{ padding: isMobile ? "20px 18px 10px" : "22px 28px 10px", fontSize: isMobile ? 16 : 18, color:"var(--ink-soft)", lineHeight:1.75 }}>
              {r.story.split("\n").map((line, i) =>
                line === "—"
                  ? <hr key={i} style={{ border:"none", borderTop:"1px solid var(--border)", margin:"14px 0" }} />
                  : line === ""
                    ? <br key={i} />
                    : <p key={i} style={{ marginBottom: 6 }}>{line}</p>
              )}
            </div>
            <div style={{ padding:"4px 28px 0" }}>
              <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, letterSpacing:"0.12em", color:"var(--gold)", textTransform:"uppercase", display:"block", marginBottom:8, fontWeight:600 }}>Crew</span>
              <div style={{ fontSize:14, color:"var(--ink-soft)", lineHeight:1.6 }}>
                {(r.crew||[]).join(" · ")}
              </div>
            </div>
            {r.photos?.length>0 && (
              <PhotoCarousel
                photos={r.photos}
                isMobile={isMobile}
                onOpen={(i) => setLightbox({ photos: r.photos, index: i })}
              />
            )}

            {/* Crew photo upload + share */}
            <div style={{ padding: isMobile ? "14px 18px 0" : "14px 28px 0", display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
              <label style={{ display:"inline-flex", alignItems:"center", gap:7, cursor: crewUploading===r.id ? "default" : "pointer", background:"transparent", color:"var(--slate)", border:"1px dashed var(--border)", borderRadius:999, padding:"7px 16px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, opacity: crewUploading===r.id ? 0.6 : 1 }}>
                {crewUploading===r.id ? "Uploading…" : "📷 Add your photos"}
                <input
                  type="file" accept="image/*" multiple
                  disabled={crewUploading===r.id}
                  style={{ display:"none" }}
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    if (!files.length) return;
                    setCrewUploading(r.id);
                    try {
                      const urls = await uploadImages(files, `pmf-races/race-${r.raceNo}-crew`);
                      if (urls.length) await onCrewPhotos(r.id, urls);
                    } catch (err) {
                      alert("Upload failed: " + err.message);
                    }
                    setCrewUploading(null);
                    e.target.value = "";
                  }}
                />
              </label>
              <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, color:"var(--slate)" }}>JPG or PNG</span>
            </div>

            {/* Reactions + comments */}
            {(() => {
              const pad = isMobile ? "18px" : "28px";
              const raceRx = (reactions || []).filter(x => x.race_date === r.date);
              const raceCm = (comments || []).filter(x => x.race_date === r.date);
              return (
                <div style={{ padding: `20px ${pad} 0` }}>
                  {/* Reaction buttons */}
                  <div style={{ display:"flex", flexWrap:"wrap", gap:"8px 20px" }}>
                    {REACTIONS.map(rx => {
                      const these = raceRx.filter(x => x.emoji === rx.emoji);
                      const count = these.length;
                      return (
                        <button
                          key={rx.emoji}
                          onClick={() => { setReactPrompt({ date: r.date, emoji: rx.emoji }); setReactNameInput(savedName); }}
                          title={these.map(x=>x.name).join(", ")}
                          style={{ display:"inline-flex", alignItems:"center", gap:6, background:"transparent", border:"none", padding:"2px 0", cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:14, fontWeight:600, color: count ? "var(--text)" : "var(--slate)", borderBottom:`2px solid ${count ? "var(--gold)" : "transparent"}` }}
                        >
                          <span style={{ fontSize:16 }}>{rx.emoji}</span>
                          <span>{rx.label}</span>
                          {count>0 && <span style={{ fontWeight:800, color:"var(--slate)" }}>{count}</span>}
                        </button>
                      );
                    })}
                  </div>

                  {/* Comments */}
                  <div style={{ marginTop:18 }}>
                    <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.18em", textTransform:"uppercase", color:"var(--gold-deep, #8B6508)", fontWeight:700, marginBottom:10 }}>
                      Comments{raceCm.length>0 ? ` (${raceCm.length})` : ""}
                    </div>
                    {raceCm.map(c => {
                      const mineComment = isMyComment(c.id);
                      const isEditing = editingComment?.id === c.id;
                      return (
                        <div key={c.id} style={{ marginBottom:12 }}>
                          <div style={{ display:"flex", alignItems:"baseline", gap:8 }}>
                            <span style={{ fontWeight:700, color:"var(--text)", fontSize:14 }}>{c.name}</span>
                            <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, color:"var(--slate)" }}>{fmtDate(c.created_at?.slice(0,10))}</span>
                            {mineComment && !isEditing && (
                              <span style={{ marginLeft:"auto", display:"flex", gap:10 }}>
                                <button onClick={() => setEditingComment({ id: c.id, text: c.text })} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--slate)", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, letterSpacing:"0.08em", textTransform:"uppercase", fontWeight:700, padding:0 }}>Edit</button>
                                <button onClick={() => setRemoveCommentConfirm({ id: c.id })} style={{ background:"none", border:"none", cursor:"pointer", color:"#C0392B", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, letterSpacing:"0.08em", textTransform:"uppercase", fontWeight:700, padding:0 }}>Delete</button>
                              </span>
                            )}
                          </div>
                          {isEditing ? (
                            <div style={{ marginTop:6 }}>
                              <textarea
                                value={editingComment.text}
                                onChange={e => setEditingComment({ ...editingComment, text: e.target.value })}
                                rows={2}
                                style={{ width:"100%", background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:8, padding:"9px 11px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:14, color:"var(--ink)", outline:"none", resize:"vertical", lineHeight:1.5, boxSizing:"border-box" }}
                              />
                              <div style={{ display:"flex", gap:8, marginTop:6 }}>
                                <button
                                  onClick={async () => {
                                    const t = editingComment.text.trim();
                                    if (t) { await onUpdateComment(c.id, t); }
                                    setEditingComment(null);
                                  }}
                                  style={{ background: btnGradient, color:"var(--gold-lt)", border:"none", borderRadius:6, padding:"7px 16px", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}
                                >Save</button>
                                <button onClick={() => setEditingComment(null)} style={{ background:"transparent", color:"var(--slate)", border:"1px solid var(--border)", borderRadius:6, padding:"7px 14px", fontWeight:600, fontSize:12, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ fontSize:15, color:"var(--ink-soft)", marginTop:2, lineHeight:1.5 }}>{c.text}</div>
                          )}
                        </div>
                      );
                    })}

                    {/* Add comment — opens modal */}
                    <button
                      onClick={() => { setCommentModal({ date: r.date }); setCmName(savedName); setCmText(""); setAiGenCount(0); setAiLimitMsg(false); }}
                      style={{ marginTop:12, display:"inline-flex", alignItems:"center", gap:7, background:"var(--surface2)", color:"var(--text)", border:"1px solid var(--border)", borderRadius:999, padding:"9px 18px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, letterSpacing:"0.08em", fontWeight:600, cursor:"pointer" }}
                    >💬 Add a comment <AiSparkle size={13} /></button>

                    {/* Season pics on its own line (recap sharing moved to the header icon) */}
                    <div style={{ marginTop:16, paddingTop:14, borderTop:"1px solid var(--border)", display:"flex", alignItems:"center", gap:18, flexWrap:"wrap" }}>
                      <a href={ICLOUD_ALBUM} target="_blank" rel="noreferrer" style={{ display:"inline-flex", alignItems:"center", gap:5, fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.1em", textTransform:"uppercase", color:"var(--slate)", fontWeight:600, textDecoration:"none" }}>📷 Season pics</a>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div style={{ height:26 }} />
          </article>{!nextOnTop && i === 0 && nextRaceBlock}</React.Fragment>
        ))}
        </div>

        </>}
      </div>
      <footer style={{ padding:"18px 0 26px", textAlign:"center", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.12em", color:"var(--slate)", textTransform:"uppercase" }}>
        © Savoye Studio 2026
      </footer>
      </div>{/* end scroll area */}

      {/* ── Mobile bottom tab bar — floating rounded bar (flex sibling, Safari-proof) ── */}
      {isMobile && (
        <nav style={{ flexShrink:0, background:"var(--bg)", padding:"8px 12px calc(10px + env(safe-area-inset-bottom))" }}>
          <div style={{ display:"flex", background:"var(--surface)", border:"1px solid var(--border)", borderRadius:22, boxShadow:"0 6px 24px rgba(11,22,38,0.16)", overflow:"hidden" }}>
            {[
              { id:"feed",  label:"Feed" },
              { id:"recap", label:"Season" },
              { id:"stats", label:"Stats" },
              { id:"boat",  label:"Boat" },
            ].map(t => (
              <button key={t.id} onClick={() => { goTab(t.id); document.querySelector('[data-pmf-scroll]')?.scrollTo({ top:0, behavior:"auto" }); }} style={{
                flex:1, padding:"10px 4px", background:"none", border:"none", cursor:"pointer",
              }}>
                <div style={{ lineHeight:0, transform: activeTab===t.id ? "scale(1.1)" : "scale(1)", transition:"transform 0.15s", opacity: activeTab===t.id ? 1 : 0.55 }}><NavIcon id={t.id} active={activeTab===t.id} size={26} /></div>
                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, letterSpacing:"0.06em", textTransform:"uppercase", marginTop:5, fontWeight: activeTab===t.id ? 700 : 600, color: activeTab===t.id ? "var(--text)" : "var(--slate)" }}>{t.label}</div>
              </button>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}

const td = { padding:"10px 14px", color:"var(--ink-soft)", whiteSpace:"nowrap", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:14, fontWeight:500 };
function fmtDate(iso){ return new Date(iso+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"}); }
function fmtDateLong(iso){ return new Date(iso+"T12:00:00").toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"}); }
function isRaceToday(iso){
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;
  return iso === today;
}
// Extract just the main angle from a TWD string like "207° SSW (σ 71°)" → "207°"
function mainAngle(twd){
  if(!twd) return "—";
  const m = String(twd).match(/(\d+)\s*°?/);
  return m ? `${m[1]}°` : String(twd);
}

function ordinalSuffix(n){
  if (n == null) return "";
  const s = ["th","st","nd","rd"], v = n % 100;
  return s[(v-20)%10] || s[v] || s[0];
}

// A short, plain-language summary of the season so far.

